"use client";

import React, { useState, useRef, MouseEvent } from "react";
import { ArrowRight, UtensilsCrossed } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const menuItems = [{
  id: 1,
  name: "Sous Chef",
  description: "Lead our culinary innovation. Requires 5+ years in Michelin-starred environments and a passion for Danish terroir.",
  price: "Full-time",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/career_sous_chef.png",
  category: "Kitchen"
}, {
  id: 2,
  name: "Sommelier",
  description: "Curate our natural wine program. Deep knowledge of Nordic producers and biodynamic viticulture required.",
  price: "Full-time",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/career_sommelier.png",
  category: "Cellar"
}, {
  id: 3,
  name: "Pastry Artist",
  description: "Craft sculptural desserts using foraged berries and ancient grains. Expertise in fermentation preferred.",
  price: "Full-time",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/career_pastry.png",
  category: "Bakery"
}, {
  id: 4,
  name: "FOH Captain",
  description: "Orchestrate the guest journey with minimalist grace. Fluent in English and Danish with a focus on empathy.",
  price: "Part-time",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/career_service.png",
  category: "Service"
}, {
  id: 5,
  name: "Commis Chef",
  description: "Begin your journey in New Nordic cuisine. We offer rigorous training and a path to culinary mastery.",
  price: "Apprentice",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/career_commis.png",
  category: "Kitchen"
}];
const TiltCard = ({
  item,
  index
}: {
  item: typeof menuItems[0];
  index: number;
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [rotation, setRotation] = useState({
    x: 0,
    y: 0
  });
  const [isHovering, setIsHovering] = useState(false);
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / centerY * -10;
    const rotateY = (x - centerX) / centerX * 10;
    setRotation({
      x: rotateX,
      y: rotateY
    });
  };
  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => {
    setIsHovering(false);
    setRotation({
      x: 0,
      y: 0
    });
  };
  return <div data-section-id="10576" ref={cardRef} onMouseMove={handleMouseMove} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="relative h-[500px] w-full perspective-1000 group cursor-pointer" data-index={index}>
      <div className="relative h-full w-full transition-transform duration-200 ease-out preserve-3d" style={{
      transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`
    }}>
        {}
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-20 w-64 h-64 md:w-72 md:h-72 transition-transform duration-500 ease-out group-hover:scale-110 group-hover:-translate-y-4 drop-shadow-2xl" style={{
        transform: `translateZ(50px) translateX(-50%) ${isHovering ? 'scale(1.1) translateY(-1rem)' : ''}`
      }}>
           <img src={item.image} alt={item.name} className="w-full h-full object-contain drop-shadow-[0_20px_30px_rgba(0,0,0,0.3)]" />
        </div>

        {}
        <Card className="absolute bottom-0 left-0 right-0 h-[380px] w-full overflow-hidden border-black/10 bg-black/5 backdrop-blur-md shadow-xl transition-colors duration-300 group-hover:bg-black/10 group-hover:border-black/20">
          <CardContent className="flex h-full flex-col items-center justify-end pb-10 pt-32 px-6 text-center">
            <span className="mb-2 inline-block rounded-full bg-black/10 px-3 py-1 text-xs font-mono tracking-widest text-black uppercase">
              {item.category}
            </span>
            <h3 className="font-serif text-3xl font-medium text-black mb-3 tracking-tight">
              {item.name}
            </h3>
            <p className="font-sans text-black/70 text-sm leading-relaxed mb-6 line-clamp-3">
              {item.description}
            </p>
            <div className="mt-auto flex items-center gap-4">
              <span className="font-mono text-xl text-black">{item.price}</span>
              <div className="h-px w-12 bg-black/20"></div>
              <Button variant="ghost" size="sm" className="text-xs border-black/20 hover:bg-black/10 hover:text-black" asChild>
                <Link to="/TheKitchenPage">Apply</Link>
              </Button>
            </div>
          </CardContent>
          
          {}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-black/5 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black/10 to-transparent pointer-events-none"></div>
        </Card>
      </div>
    </div>;
};
export default function TheCurrentRotation() {
  return <section id="join-the-vision" className="bg-primary text-black py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-black/5 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-black/5 rounded-full blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-black mb-4">
              <UtensilsCrossed className="w-5 h-5" />
              <span className="text-sm font-mono uppercase tracking-widest">Careers at NordicBite</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium leading-tight text-black">
              Join the Vision
            </h2>
            <p className="mt-6 text-lg text-black/80 font-sans max-w-xl leading-relaxed">
              We are seeking avant-garde thinkers and culinary artisans to help us redefine the boundaries of New Nordic gastronomy.
            </p>
          </div>
          
          <div className="flex-shrink-0">
            <Button variant="outline" className="group border-black/30 hover:bg-black/10 hover:border-black text-black" asChild>
              <Link to="/TheKitchenPage">
                Explore All Roles
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div className="w-full py-10">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-8 pb-10">
              {menuItems.map((item, index) => <CarouselItem key={item.id} className="pl-4 md:pl-8 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                  <TiltCard item={item} index={index} />
                </CarouselItem>)}
            </CarouselContent>
            <div className="flex justify-end gap-2 mt-4 pr-4">
              <CarouselPrevious className="static translate-y-0 translate-x-0 border-black/10 hover:bg-black/10 hover:text-black" />
              <CarouselNext className="static translate-y-0 translate-x-0 border-black/10 hover:bg-black/10 hover:text-black" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}